import { useGetSessionToken, useSetupLanguage } from '@homestake/shared/hooks';
import { ErrorScreen } from '@homestake/shared/ui';
import { observer, use$ } from '@legendapp/state/react';
import { type ReactNode, useEffect, useState } from 'react';
import {
  useBackendErrorNotification,
  useCreateDemoSession,
  useInitGame,
  usePreloadAssets,
} from '../hooks';
import { sessionState$ } from '../state/session.state';
import { LoadingScreen } from './LoadingScreen';

export const Init = observer(({ children }: { children: ReactNode }) => {
  const gameRoundId = use$(sessionState$.gameRoundId);
  const { showBackendErrorNotification } = useBackendErrorNotification();
  const [isActualLoadingComplete, setIsActualLoadingComplete] = useState(false);
  const [showChildren, setShowChildren] = useState(false);

  const { isLoading: isPreloadingAssets } = usePreloadAssets();

  useSetupLanguage();

  const { token, setToken } = useGetSessionToken();

  const { mutate: createDemoSession, isPending: isCreatingDemoSession } =
    useCreateDemoSession({
      onSuccess: (data) => {
        try {
          // Parse the returned URL to extract query parameters
          const url = new URL(data);
          const queryParams = url.searchParams;

          // Extract the token from the demo session URL
          const demoToken = queryParams.get('token');

          if (demoToken) {
            // Use nuqs to update the token parameter without page reload
            setToken(demoToken);
          }

          // Set any other query parameters if needed
          for (const [key, value] of queryParams) {
            if (key !== 'token') {
              // For other parameters, we can still update the URL manually
              // or use additional nuqs hooks if they need to be reactive
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.set(key, value);
              window.history.replaceState({}, '', currentUrl.toString());
            }
          }
        } catch (error) {
          console.error('Error updating URL with demo session:', error);
        }
      },
      onError: (error) => {
        showBackendErrorNotification(error);
      },
    });

  // Create a demo session if there's no token
  useEffect(() => {
    if (!(token || isCreatingDemoSession)) {
      createDemoSession();
    }
  }, [token, isCreatingDemoSession, createDemoSession]);

  // Initialize the game with the token
  const { isLoading, isError } = useInitGame(token);

  // Check if all actual loading is complete
  const isAllLoadingComplete =
    !(isLoading || isCreatingDemoSession) &&
    token &&
    gameRoundId &&
    !isPreloadingAssets;

  // Update loading completion state
  useEffect(() => {
    if (isAllLoadingComplete && !isActualLoadingComplete) {
      setIsActualLoadingComplete(true);
    }
  }, [isAllLoadingComplete, isActualLoadingComplete]);

  // Handle when loading screen animation is complete
  const handleLoadingComplete = () => {
    setShowChildren(true);
  };

  // Show error message if there's an error
  if (isError) {
    return <ErrorScreen />;
  }

  // Show children only after loading screen animation is complete
  if (showChildren) {
    return <>{children}</>;
  }

  // Show loading screen with controlled completion
  return (
    <LoadingScreen
      onProgressComplete={handleLoadingComplete}
      shouldCompleteProgress={isActualLoadingComplete}
    />
  );
});
